import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import TextField from '@mui/material/TextField';
import { Grid, Link, Button, Alert, Typography, CircularProgress, Box } from '@mui/material';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import AddIcon from '@mui/icons-material/Add';
import { FullScreenCircularLoader } from 'src/App/Components/Loaders';
import { RED_BUTTON_COLOR, RED_BUTTON_COLOR_HOVER } from 'src/colors';
import { airbnbManageYourSpaceUrls } from 'src/const';
import { callApi } from 'src/core/callApi';
import UserAirbnbListingPage from 'src/App/Dashboards/Pricing/UserAirbnbListingPage';
import { useSelector } from 'react-redux';

const DynamicPricingDashboard = matches => {
  const [airbnbURL, setAirbnbURL] = useState<String>();
  const [airbnbURLValid, setAirbnbURLValid] = useState<Boolean>(true);
  const [showUnableToFetchListingAlert, setShowUnableToFetchListingAlert] = useState<Boolean>(
    false
  );
  const [isConnectAirbnbAccountCollapsed, setIsConnectAirbnbAccountCollapsed] = useState<Boolean>(
    false
  );
  // const [activeListingIDFutureRates, setActiveListingIDFutureRates] = useState({});
  const [userAirbnbListingId, setUserAirbnbListingId] = useState<String>();
  const [userListings, setUserListings] = useState<any>();
  const [showUserListingsFetchingLoader, setShowUserListingsFetchingLoader] = useState<Boolean>(
    false
  );
  const [showUserListingsFetchingMiniLoader, setShowUserListingsFetchingMiniLoader] = useState<
    Boolean
  >(false);
  const [
    userListingsPricingRecommendationsResult,
    setUserListingsPricingRecommendationsResult,
  ] = useState<any[]>([]);

  const user = useSelector(state => (state as any).dashboard.user);
  const userEmail = user.email;

  if ((window as any).Tawk_API) {
    (window as any).Tawk_API.onLoad = () => {
      (window as any).Tawk_API.hideWidget();
    };
  }

  useEffect(() => {
    setAirbnbURL(user['airbnbManageListingURL'] || '');
  }, [user]);

  useEffect(() => {
    if (userListings) {
      if (
        userListingsPricingRecommendationsResult.length > 0 &&
        userListings.data.length > userListingsPricingRecommendationsResult.length
      ) {
        setShowUserListingsFetchingMiniLoader(true);
      } else {
        setShowUserListingsFetchingMiniLoader(false);
      }
    }
  }, [userListings, userListingsPricingRecommendationsResult]);

  useEffect(() => {
    // Function to clear the results
    const clearResults = () => {
      setUserListingsPricingRecommendationsResult([]);
    };

    if (userListings) {
      // Clear results when userListings changes
      clearResults();

      const ids = userListings.data.map(listing => listing.listingID);

      async function fetchDataWithRetry(startIndex, retryCount = 3) {
        const batchSize = 25;
        const batchIds = ids.slice(startIndex, startIndex + batchSize);

        const retry = async (id, retryCount) => {
          const URL =
            'https://5v07hdzm7g.execute-api.us-east-2.amazonaws.com/default/getListingPricingRecommendation';
          try {
            const response = await callApi(URL, { queryParams: { listingID: id } }, true);
            if (Object.keys(response).length > 0) {
              setUserListingsPricingRecommendationsResult(previousResults => [
                ...previousResults,
                response,
              ]);
            } else {
              console.log(response, typeof response);
              throw new Error();
            }
          } catch (error) {
            console.error('Error:', error);
            if (retryCount > 0) {
              await retry(id, retryCount - 1);
            }
          }
        };

        const batchPromises = batchIds.map(id => retry(id, retryCount));

        await Promise.all(batchPromises);

        // Continue fetching the next batch
        const nextIndex = startIndex + batchSize;
        if (nextIndex < ids.length) {
          fetchDataWithRetry(nextIndex, retryCount);
        }
      }

      // Start fetching the first batch
      fetchDataWithRetry(0);
    } else {
      // If userListings is falsy, clear the results
      clearResults();
    }
  }, [userListings]);

  useEffect(() => {
    if (userListingsPricingRecommendationsResult.length > 0 || showUnableToFetchListingAlert) {
      setShowUserListingsFetchingLoader(false);
      if (showUnableToFetchListingAlert) {
        setUserListingsPricingRecommendationsResult([]);
      }
    }
  }, [userListingsPricingRecommendationsResult, showUnableToFetchListingAlert]);

  const handleConnectAirbnbAccountURL = () => {
    if (verifyAirbnbManageYourSpaceUrl()) {
      setAirbnbURLValid(true);
      setUserAirbnbListingId(airbnbURL?.split('/')[4]);
      setIsConnectAirbnbAccountCollapsed(true);
    } else {
      setAirbnbURLValid(false);
      setIsConnectAirbnbAccountCollapsed(false);
    }
  };

  // const callListingfutureRateScraper = async () => {
  //   const URL = 'https://mef92b6v95.execute-api.us-east-2.amazonaws.com/default/futureRateScrapper';
  //   const response = await fetch(URL, {
  //     method: 'POST',
  //     body: JSON.stringify({ listingID: activeListingID }),
  //   });
  //   const data = await response.json();
  //   if (data.data !== 'invalid listing_id' && data.data !== 'calendar scrapping failed') {
  //     setActiveListingIDFutureRates(data.data);
  //   }
  // };

  const verifyAirbnbManageYourSpaceUrl = () => {
    let isUrlVerified = false;
    airbnbManageYourSpaceUrls.forEach(url => {
      if (airbnbURL?.includes(url)) {
        isUrlVerified = true;
      }
    });
    return isUrlVerified;
  };

  const fetchUserAirbnbListing = async () => {
    try {
      setShowUserListingsFetchingLoader(true);
      const URL = 'https://b62nudr14e.execute-api.us-east-2.amazonaws.com/default/getUserListings';
      const data = await callApi(
        URL,
        { body: { listingID: userAirbnbListingId, URL: airbnbURL, email: userEmail } },
        false,
        false,
        '',
        'POST'
      );
      if (data.data != 'fetching listings failed' && data.data.length > 0) {
        setShowUnableToFetchListingAlert(false);
        setUserListings(data);
      } else {
        setShowUnableToFetchListingAlert(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    if (userAirbnbListingId) {
      fetchUserAirbnbListing();
    }
  }, [userAirbnbListingId]);

  return (
    <>
      {/* title and meta tags */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Bright Pricing</title>
        <meta
          name="description"
          content="Real-time analytics & dynamic pricing tool for Airbnb revenue managers"
        />
      </Helmet>

      <Grid
        container
        direction="column"
        spacing={2}
        style={{ position: 'relative', top: 0, left: 0, padding: matches ? '20px' : '40px' }}
      >
        <Grid
          container
          item
          direction="column"
          spacing={2}
          sx={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          <Grid item sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              sx={{ border: '2px solid lightGray' }}
              onClick={() => {
                setIsConnectAirbnbAccountCollapsed(!isConnectAirbnbAccountCollapsed);
              }}
            >
              <AddIcon />
              Connect Account{' '}
              {!isConnectAirbnbAccountCollapsed ? (
                <KeyboardDoubleArrowUpIcon />
              ) : (
                <KeyboardDoubleArrowDownIcon />
              )}
            </Button>
          </Grid>
          <Grid
            item
            sx={{
              border: !isConnectAirbnbAccountCollapsed ? '2px solid lightGray' : '',
              height: !isConnectAirbnbAccountCollapsed ? '220px' : '0px',
              overflow: 'hidden',
              transition: 'height 0.3s ease',
              padding: '5px 15px !important',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Connect your Airbnb Account
            </Typography>
            <Typography variant="body2">
              Enter the Airbnb URL From a listing.
              <Link
                href="https://www.airbnb.co.in/manage-your-space"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'black',
                  borderBottom: '1px solid black',
                  textDecoration: 'none',
                }}
                sx={{ fontWeight: 400, marginLeft: '5px', '&:hover': { cursor: 'pointer' } }}
              >
                Go to your Airbnb Listings.
              </Link>
            </Typography>
            <TextField
              id="outlined-basic"
              label={airbnbURL ? '' : 'https://www.airbnb.com/manage-your-space/345'}
              variant="outlined"
              value={airbnbURL}
              fullWidth
              sx={{ marginTop: '10px' }}
              onChange={e => {
                setAirbnbURL(e.target.value);
              }}
            />
            {!airbnbURLValid && (
              <Alert severity="error" sx={{ marginTop: '5px' }}>
                Invalid URL!
              </Alert>
            )}
            <Button
              sx={{
                marginTop: '10px',
                width: '100%',
                color: 'white',
                backgroundColor: RED_BUTTON_COLOR,
                '&:hover': { cursor: 'pointer', backgroundColor: RED_BUTTON_COLOR_HOVER },
              }}
              onClick={handleConnectAirbnbAccountURL}
            >
              Connect Airbnb Account
            </Button>
          </Grid>
        </Grid>

        {showUserListingsFetchingLoader && (
          <FullScreenCircularLoader message="We are fetching your listings -  just 30 seconds to go!" />
        )}

        <Grid container item direction="column" spacing="2" sx={{ margiTop: 10 }}>
          {showUnableToFetchListingAlert && (
            <Box>
              <Typography variant="h5">
                Unfortunately, Your listings cannot be retrieved at this time. 😔
              </Typography>
            </Box>
          )}

          {userListingsPricingRecommendationsResult.length > 0 ? (
            <UserAirbnbListingPage
              data={userListingsPricingRecommendationsResult}
              showUserListingsFetchingMiniLoader={showUserListingsFetchingMiniLoader}
            />
          ) : null}
        </Grid>
      </Grid>
    </>
  );
};

export default DynamicPricingDashboard;
